import React, { useEffect, useState } from 'react';

import { useMediaQuery } from 'react-responsive';

import Text from 'common/components/Text/Text';

import styles from 'features/user/dashboard/pages/dashboard/Dashboard.module.scss';
import { ReactComponent as StepsRobot } from 'assets/session/dashboard/steps_robot.svg';
import { useAppSelector } from 'app/hooks';
import { setLastStepDone, setListOfProgramsAsked, UserState } from '../userSlice';
import { getPercentageNextSteps, getStepsToShow, navigateTop } from 'common/utils';
import { useGetPostulationsQuery, useUpdateNextStepsMutation } from 'services/exploreApi';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Postulation } from 'app/types';

interface stepProps {
  text: string;
  index: number;
  postulation: Postulation;
  done: boolean;
}

const StepComponent = ({ text, index, postulation, done }: stepProps): React.ReactElement => {
  const [updateNextSteps] = useUpdateNextStepsMutation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const handleSubmitForm = () => {
    const values = { list_of_programs_asked: true };
    updateNextSteps(values).then(async (resp: any) => {
      dispatch(setListOfProgramsAsked(true));
      dispatch(setLastStepDone(4));
    });
  };

  const renderShortlist = () => {
    navigate('/home/shorlist'); // DEBUG
    setTimeout(() => {
      navigateTop(0, 0, 'topics');
    }, 100);
  };

  const handleStartStep = (index: number) => {
    if (index == 0) {
      navigate('personal_information');
    } else if (index == 1) {
      navigate('preferences');
    } else if (index == 2) {
      window.open('/experts/request-meeting');
    } else if (index == 3) {
      handleSubmitForm();
    } else if (index == 4) {
      navigate('/explore');
    } else if (index == 5) {
      renderShortlist();
    } else {
      navigate(`/home/applications/${postulation.id}`);
    }
  };
  const navigate = useNavigate();
  return (
    <div className={styles.stepComponent}>
      <div className={styles.stepContainer}>
        <div className={done ? styles.iconContainerDone : styles.iconContainer}>
          <i className="bi bi-check-circle"> </i>
        </div>
        {!done ? (
          <div onClick={() => handleStartStep(index)}>
            <Text size={!isMobile ? 20 : 14} weight={400}>
              {text}
            </Text>
          </div>
        ) : (
          <Text
            size={!isMobile ? 20 : 14}
            weight={400}
            inlineStyle={{ textDecoration: 'line-through' }}
          >
            {text}
          </Text>
        )}
      </div>
      {!isMobile && !done && (
        <div className={`${styles.button}`} onClick={() => handleStartStep(index)}>
          <Text size={16} weight={500}>
            Start
          </Text>
        </div>
      )}
    </div>
  );
};

const NextStepsCard = (): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { data: postulationsList, refetch } = useGetPostulationsQuery();
  const [firstPostulation, setFirstPostulation] = useState<Postulation>(
    postulationsList ? postulationsList.data.postulations.first : undefined
  );
  const { lastStepDone } = useAppSelector(({ user }: { user: UserState }) => user);
  const {
    nextSteps,
    profileCompleted,
    preferencesFilled,
    meetingScheduled,
    listOfProgramsAsked,
    schoolShortlisted,
    applicationStarted,
    cvUploaded,
    testsScoresUploaded,
    applicationSubmitted,
  } = useAppSelector(({ user }: { user: UserState }) => user);

  let percentage = getPercentageNextSteps([
    profileCompleted,
    preferencesFilled,
    meetingScheduled,
    listOfProgramsAsked,
    schoolShortlisted,
    applicationStarted,
    cvUploaded,
    testsScoresUploaded,
    applicationSubmitted,
  ]);

  let indexOfSteps = getStepsToShow(
    lastStepDone,
    profileCompleted,
    preferencesFilled,
    meetingScheduled,
    listOfProgramsAsked,
    schoolShortlisted,
    applicationStarted,
    cvUploaded,
    testsScoresUploaded,
    applicationSubmitted
  );

  useEffect(() => {
    refetch();
    setFirstPostulation(postulationsList?.data.postulations[0]);
  }, [postulationsList]);

  useEffect(() => {
    indexOfSteps = getStepsToShow(
      lastStepDone,
      profileCompleted,
      preferencesFilled,
      meetingScheduled,
      listOfProgramsAsked,
      schoolShortlisted,
      applicationStarted,
      cvUploaded,
      testsScoresUploaded,
      applicationSubmitted
    );
    percentage = getPercentageNextSteps([
      profileCompleted,
      preferencesFilled,
      meetingScheduled,
      listOfProgramsAsked,
      schoolShortlisted,
      applicationStarted,
      cvUploaded,
      testsScoresUploaded,
      applicationSubmitted,
    ]);
  }, [
    profileCompleted,
    preferencesFilled,
    meetingScheduled,
    listOfProgramsAsked,
    schoolShortlisted,
    applicationStarted,
    cvUploaded,
    testsScoresUploaded,
    applicationSubmitted,
    lastStepDone,
  ]);

  return (
    <div className={`${isMobile ? '' : 'mt-5'}`}>
      {!isMobile && (
        <Text size={22} weight={600}>
          My next steps
        </Text>
      )}
      <div className={styles.nextStepsBox}>
        <div className={styles.topSteps}>
          {isMobile ? (
            <>
              <div className={styles.progressContainer}>
                <Text size={16} weight={500}>
                  Next Steps
                </Text>
                <Text size={14} weight={600}>
                  {`${percentage}%`}
                </Text>
              </div>
            </>
          ) : (
            <div className={styles.progressContainer}>
              <div className={styles.headerStepBox}>
                <div className={styles.headerText}>
                  <Text size={20} weight={400} inlineStyle={{ marginBottom: '11px' }}>
                    Follow the steps to move forward your application
                  </Text>
                  <Text size={16} weight={600}>
                    {`${percentage}%`}
                  </Text>
                </div>
                <div className={styles.profileProgressBar}>
                  <div className={styles.barProgress} style={{ width: `${percentage}%` }}></div>
                </div>
              </div>
            </div>
          )}
          <div className={styles.robotContainer}>
            <StepsRobot />
          </div>
          {!isMobile && (
            <div className={styles.robotMessage}>
              Lovely! You are going to start a new life journey!
            </div>
          )}
        </div>
        {isMobile ? (
          <>
            <div className={styles.profileProgressBar}>
              <div className={styles.barProgress} style={{ width: `${percentage}%` }}></div>
            </div>
            <div
              id="carouselExampleIndicators"
              className={`carousel slide`}
              data-bs-ride="carousel"
            >
              <div className={`carousel-inner ${styles.carouselContainer}`}>
                {indexOfSteps.map((index) => (
                  <div
                    className={`carousel-item ${index === indexOfSteps[0] ? 'active' : ''}`}
                    key={index}
                  >
                    <StepComponent
                      text={nextSteps ? nextSteps[index] : ''}
                      key={index}
                      index={index}
                      postulation={firstPostulation}
                      done={false}
                    />
                  </div>
                ))}
              </div>

              <div>
                <button
                  className={`carousel-control-next`}
                  type="button"
                  data-bs-target="#carouselExampleIndicators"
                  data-bs-slide="next"
                >
                  {/* <span
                    className="carousel-control-next-icon"
                    aria-hidden="true"
                    style={{ backgroundImage: 'none' }}
                  ></span> */}
                  <div className={styles.arrowContainer}>
                    <i className="bi bi-chevron-compact-right"></i>
                  </div>
                  <span className="visually-hidden">Next</span>
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {lastStepDone ? (
              <>
                <StepComponent
                  index={lastStepDone}
                  text={nextSteps ? nextSteps[lastStepDone - 1] : ''}
                  postulation={firstPostulation}
                  done={true}
                />
                {indexOfSteps.map((index) => (
                  <StepComponent
                    text={nextSteps ? nextSteps[index] : ''}
                    key={index}
                    index={index}
                    postulation={firstPostulation}
                    done={false}
                  />
                ))}
              </>
            ) : (
              <>
                {indexOfSteps.map((index) => (
                  <StepComponent
                    text={nextSteps ? nextSteps[index] : ''}
                    key={index}
                    index={index}
                    postulation={firstPostulation}
                    done={false}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NextStepsCard;
